import { Ref, ref, watch } from "vue";
import { createCustomError } from "@/core/util";

export const StorageError = createCustomError("StorageError");

/** Returns an item from local storage wrapped inside a ref.
 * Any change in its value will be saved to local storage automatically.
 * If such item does not exists in local storage, the value of ref will be undefined.
 * @param key The key of the item in local storage
 * @returns Item value wrapped inside a ref
 * @throws {StorageError} If called using the same key more than once
 */
export function getSyncedItem<T>(key: string): Ref<T | undefined> {
  checkItemLoaded(key);
  const wrapper: Ref = ref(readInitialValue(key));

  watch(
    wrapper,
    (newValue) => {
      try {
        if (newValue === undefined) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, JSON.stringify(newValue));
        }
      } catch (error) {
        console.warn(`Failed to persist "${key}" to local storage.`, error);
      }
    },
    {
      deep: true,
      // OT-4948: must be synchronous, not the default "pre" (batched, async) flush.
      // Callers like setSelectedOrganizationRole() mutate this ref and then immediately
      // call window.location.reload() — an async flush can lose that race, so the reload
      // rehydrates from a stale localStorage snapshot.
      flush: "sync",
    }
  );

  return wrapper;
}

/**
 * Reads and parses a key from local storage, tolerating the same failures the write side now
 * does: storage disabled/blocked (throws on access) and a value that isn't valid JSON (a format
 * change, or a value some other code path wrote directly). Either one falls back to undefined
 * rather than taking down every caller of getSyncedItem at app boot.
 */
function readInitialValue<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch (error) {
    console.warn(`Failed to read "${key}" from local storage.`, error);
    return undefined;
  }
}

const keySet = new Set<string>();

function checkItemLoaded(key: string): void {
  if (keySet.has(key)) {
    throw new StorageError(
      `An item named ${key} is already loaded from local storage.`
    );
  }
  keySet.add(key);
}
